import { useState } from "react";
import { toast } from "sonner";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import MenuItemPhoto from "@/components/admin/menu/MenuItemPhoto";

const blank = { name: "", short_name: "", price: "", tax_percent: "5", attribute: "veg", is_available: true, description: "", details: [] };

// Mirrors the server (schemas/menu.py MenuItemDetail): the details view is a card
// on a phone, not a spec sheet.
const MAX_DETAILS = 20;
const DETAIL_LABEL_MAX = 40;
const DETAIL_VALUE_MAX = 300;
const DESCRIPTION_MAX = 1000;

// chk_menu_item_attribute (migration 044). `is_veg` is only the boolean projection of
// this, so a dialog that sends the boolean can express two of the three values and
// silently rewrites the third.
const ATTRIBUTES = [["veg", "Vegetarian"], ["egg", "Contains egg"], ["non_veg", "Non-vegetarian"]];

// .toggle-row is (0,1,0) and loses to .workflow-form label (0,1,1), which forces
// flex-direction:column — the caption ended up above a checkbox stretched to the full
// dialog width. Fixed here rather than in the shared sheet so the change cannot move
// any other screen.
const TOGGLE_ROW = { flexDirection: "row", alignItems: "center", justifyContent: "space-between" };
const TOGGLE_BOX = { width: 20, height: 20, minHeight: 0, flex: "0 0 auto" };

/**
 * Create or edit a menu item against /api/menu/items with in-place validation (DESIGN.md §5.4).
 */
export default function MenuItemDialog({ item, categories, onSave, onClose, children, readOnly = false, onPhotoChanged }) {
  const [form, setForm] = useState(
    item
      ? {
          name: item.name,
          short_name: item.short_name || "",
          price: String(item.price ?? ""),
          tax_percent: String(item.tax_percent ?? "5"),
          attribute: item.attribute || (item.is_veg ? "veg" : "non_veg"),
          is_available: item.is_available !== false,
          category_id: item.category_id,
          description: item.description || "",
          details: (item.details || []).map(d => ({ label: d.label || "", value: d.value || "" })),
        }
      : { ...blank, category_id: categories?.[0]?.id }
  );
  const [errors, setErrors] = useState({});
  const [, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (key, value) => {
    setForm(current => ({ ...current, [key]: value }));
    if (errors[key]) {
      setErrors(current => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    const price = Number(form.price);
    if (form.price === "" || !Number.isFinite(price) || price < 0) {
      errs.price = "Price must be zero or more";
    }
    const tax = Number(form.tax_percent);
    if (form.tax_percent === "" || !Number.isFinite(tax) || tax < 0 || tax > 100) {
      errs.tax_percent = "Tax must be between 0 and 100";
    }
    if (!form.category_id) errs.category_id = "Pick a category";
    const halfFilled = form.details.findIndex(d => Boolean(d.label.trim()) !== Boolean(d.value.trim()));
    if (halfFilled >= 0) errs.details = `Detail ${halfFilled + 1} needs both a label and a value`;
    return errs;
  };

  const handleBlur = field => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const errs = validate();
    if (errs[field]) {
      setErrors(prev => ({ ...prev, [field]: errs[field] }));
    }
  };

  const submit = async (addAnother = false) => {
    const errs = validate();
    setErrors(errs);
    setTouched({ name: true, price: true, tax_percent: true, category_id: true });
    if (Object.keys(errs).length > 0) {
      const firstField = Object.keys(errs)[0];
      const targetTestId =
        firstField === "category_id"
          ? "menu-item-category-select"
          : firstField === "tax_percent"
          ? "menu-item-tax-input"
          : firstField === "details"
          ? "menu-item-details-section"
          : `menu-item-${firstField}-input`;
      const el = document.querySelector(`[data-testid="${targetTestId}"]`);
      el?.focus();
      return;
    }

    setSaving(true);
    const result = await onSave(item?.id ?? null, {
      name: form.name.trim(),
      short_name: form.short_name.trim() || null,
      price: Number(form.price),
      tax_percent: Number(form.tax_percent),
      attribute: form.attribute,
      is_available: form.is_available,
      category_id: Number(form.category_id),
      description: form.description.trim() || null,
      details: form.details
        .map(d => ({ label: d.label.trim(), value: d.value.trim() }))
        .filter(d => d.label && d.value),
    });
    setSaving(false);
    if (!result.ok) {
      setErrors({ form: result.error });
      return;
    }
    toast.success(item ? `${form.name} updated` : `${form.name} added`);
    if (addAnother && !item) {
      setForm({ ...blank, category_id: form.category_id });
      setErrors({});
      setTouched({});
      const nameInput = document.querySelector('[data-testid="menu-item-name-input"]');
      nameInput?.focus();
    } else {
      onClose();
    }
  };

  const footerContent = readOnly ? (
    <div className="flex items-center justify-end w-full">
      <Button variant="outline" onClick={onClose} data-testid="close-menu-item-button">
        Close
      </Button>
    </div>
  ) : (
    <div className="flex items-center justify-end gap-2 w-full">
      <Button
        type="button"
        variant="outline"
        onClick={onClose}
        disabled={saving}
        data-testid="cancel-menu-item-button"
      >
        Cancel
      </Button>
      {!item && (
        <Button
          type="button"
          variant="outline"
          onClick={() => submit(true)}
          disabled={saving}
          data-testid="save-add-another-menu-item-button"
        >
          Save & Add Another
        </Button>
      )}
      <Button
        variant="action"
        onClick={() => submit(false)}
        disabled={saving}
        data-testid="save-menu-item-button"
      >
        {item ? "Save Changes" : "Add Item"}
      </Button>
    </div>
  );

  return (
    <Modal
      title={item ? `Edit ${item.name}` : "Add Menu Item"}
      onClose={onClose}
      testId="menu-item-dialog"
      footer={footerContent}
    >
      <div className="workflow-form">
        {errors.form && (
          <div className="text-meta text-danger bg-danger-subtle border border-danger/30 p-2 rounded-md mb-2" role="alert">
            {errors.form}
          </div>
        )}

        <label className="flex flex-col gap-1 text-meta font-medium">
          <span>Name *</span>
          <Input
            value={form.name}
            onChange={e => set("name", e.target.value)}
            onBlur={() => handleBlur("name")}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? "menu-item-name-error" : undefined}
            className={cn(errors.name && "border-danger focus:border-danger ring-danger/20")}
            data-testid="menu-item-name-input"
          />
          {errors.name && (
            <span id="menu-item-name-error" className="text-meta text-danger">
              {errors.name}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-1 text-meta font-medium">
          <span>Short code</span>
          <Input
            value={form.short_name}
            onChange={e => set("short_name", e.target.value)}
            placeholder="PT"
            data-testid="menu-item-code-input"
          />
        </label>

        <label className="flex flex-col gap-1 text-meta font-medium">
          <span>Category *</span>
          <select
            value={form.category_id ?? ""}
            onChange={e => set("category_id", e.target.value)}
            onBlur={() => handleBlur("category_id")}
            aria-invalid={Boolean(errors.category_id)}
            aria-describedby={errors.category_id ? "menu-item-category-error" : undefined}
            className={cn(
              "w-full h-9 px-3 rounded-md border bg-background text-body focus:ring-1 focus:ring-primary outline-none",
              errors.category_id && "border-danger"
            )}
            data-testid="menu-item-category-select"
          >
            {categories?.map(category => (
              <option value={category.id} key={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {errors.category_id && (
            <span id="menu-item-category-error" className="text-meta text-danger">
              {errors.category_id}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-1 text-meta font-medium">
          <span>Price (₹) *</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.price}
            onChange={e => set("price", e.target.value)}
            onBlur={() => handleBlur("price")}
            aria-invalid={Boolean(errors.price)}
            aria-describedby={errors.price ? "menu-item-price-error" : undefined}
            className={cn(errors.price && "border-danger focus:border-danger ring-danger/20")}
            data-testid="menu-item-price-input"
          />
          {errors.price && (
            <span id="menu-item-price-error" className="text-meta text-danger">
              {errors.price}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-1 text-meta font-medium">
          <span>Tax (%)</span>
          <Input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.tax_percent}
            onChange={e => set("tax_percent", e.target.value)}
            onBlur={() => handleBlur("tax_percent")}
            aria-invalid={Boolean(errors.tax_percent)}
            aria-describedby={errors.tax_percent ? "menu-item-tax-error" : undefined}
            className={cn(errors.tax_percent && "border-danger focus:border-danger ring-danger/20")}
            data-testid="menu-item-tax-input"
          />
          {errors.tax_percent && (
            <span id="menu-item-tax-error" className="text-meta text-danger">
              {errors.tax_percent}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-1 text-meta font-medium">
          <span>Food type</span>
          <select
            value={form.attribute}
            onChange={e => set("attribute", e.target.value)}
            className="w-full h-9 px-3 rounded-md border bg-background text-body focus:ring-1 focus:ring-primary outline-none"
            data-testid="menu-item-attribute-select"
          >
            {ATTRIBUTES.map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="toggle-row text-meta font-medium" style={TOGGLE_ROW}>
          <span>Available</span>
          <input
            type="checkbox"
            style={TOGGLE_BOX}
            checked={form.is_available}
            onChange={e => set("is_available", e.target.checked)}
            data-testid="menu-item-available-checkbox"
          />
        </label>

        {/* The item details view staff open with a long-press or right-click
            while ordering (owner, 2026-10-20). Display only. */}
        <label className="flex flex-col gap-1 text-meta font-medium">
          <span>Description</span>
          <textarea
            value={form.description}
            maxLength={DESCRIPTION_MAX}
            onChange={e => set("description", e.target.value)}
            disabled={readOnly}
            placeholder="What the dish is, in a line or two"
            className="w-full min-h-[4rem] rounded-md border bg-background p-2 text-body focus:ring-1 focus:ring-primary outline-none"
            data-testid="menu-item-description-input"
          />
        </label>

        <div className="flex flex-col gap-1 text-meta font-medium" data-testid="menu-item-details-section" tabIndex={-1}>
          <span>Details shown to staff</span>
          <span className="text-micro text-muted-foreground font-normal">Nutrition, allergens, spice level — any label and value you want on the item's details card.</span>
          {form.details.map((d, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`menu-item-detail-${i}`}>
              <Input
                value={d.label}
                maxLength={DETAIL_LABEL_MAX}
                placeholder="Label (e.g. Allergens)"
                disabled={readOnly}
                onChange={e => set("details", form.details.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                className="w-2/5"
                data-testid={`menu-item-detail-${i}-label`}
              />
              <Input
                value={d.value}
                maxLength={DETAIL_VALUE_MAX}
                placeholder="Value (e.g. Dairy, nuts)"
                disabled={readOnly}
                onChange={e => set("details", form.details.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                className="flex-1"
                data-testid={`menu-item-detail-${i}-value`}
              />
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 text-danger"
                  onClick={() => set("details", form.details.filter((_, j) => j !== i))}
                  aria-label={`Remove detail ${i + 1}`}
                  data-testid={`menu-item-detail-${i}-remove`}
                >
                  ×
                </Button>
              )}
            </div>
          ))}
          {!readOnly && form.details.length < MAX_DETAILS && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 self-start"
              onClick={() => set("details", [...form.details, { label: "", value: "" }])}
              data-testid="menu-item-detail-add"
            >
              + Add a detail
            </Button>
          )}
          {errors.details && (
            <span className="text-meta text-danger" data-testid="menu-item-details-error">{errors.details}</span>
          )}
        </div>

        {readOnly && (
          <p className="text-meta text-muted-foreground" data-testid="menu-item-read-only">
            View only: changing items needs the menu edit permission.
          </p>
        )}
      </div>
      {/* Sections that save on their own (the menu hub passes the variations editor). */}
      {item?.id != null && (
        <div className="mt-4 border-t pt-3">
          <MenuItemPhoto item={item} readOnly={readOnly} onChanged={onPhotoChanged} />
        </div>
      )}
      {children && <div className="mt-4 border-t pt-3">{children}</div>}
    </Modal>
  );
}
